/**
 * 姿势库弹窗（P0-6b）：977 姿势的分类 + 搜索 + 缩略图网格，套用到当前选中角色。
 *
 * 对齐竞品"姿势预设"面板的能力（搜索框 + 类型下拉 + 缩略图网格），但布局走我方
 * 三栏范式下的模态实现（Modal 容器统一风格）。数据来自 loadPoseCatalog()（模块级缓存），
 * 过滤走 poseCatalog 纯函数（已离线单测），本组件只负责渲染与事件转发。
 *
 * 应用不关窗：让用户能连续试多个姿势（竞品也是这个交互），选中项高亮；关窗走右上角/关闭按钮。
 */
import { useEffect, useMemo, useState } from 'react';

import {
  ALL_CATEGORY,
  categoriesOf,
  countByCategory,
  filterPoses,
  loadPoseCatalog,
  poseThumbUrl,
  type PoseEntry,
} from './poseCatalog';
import { Modal } from '../../ui/Modal';

export interface PoseLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (pose: PoseEntry) => void;
  onRest: () => void;
  activePoseId: string | null;
}

export function PoseLibraryDialog({
  open,
  onClose,
  onApply,
  onRest,
  activePoseId,
}: PoseLibraryDialogProps) {
  const [catalog, setCatalog] = useState<PoseEntry[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [category, setCategory] = useState<string>(ALL_CATEGORY);
  const [query, setQuery] = useState('');

  // 打开时确保 catalog 已加载（loadPoseCatalog 自带缓存，重复打开不重拉）
  useEffect(() => {
    if (!open || catalog !== null) {
      return;
    }
    let cancelled = false;
    void loadPoseCatalog().then((data) => {
      if (cancelled) {
        return;
      }
      if (data === null) {
        setFailed(true);
      } else {
        setCatalog(data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, catalog]);

  const cats = useMemo(() => (catalog === null ? [] : categoriesOf(catalog)), [catalog]);
  const counts = useMemo(() => (catalog === null ? {} : countByCategory(catalog)), [catalog]);
  const results = useMemo(
    () => (catalog === null ? [] : filterPoses(catalog, { category, query })),
    [catalog, category, query],
  );

  const catBtn = 'min-h-[26px] rounded-control px-2 text-uiSm transition-colors duration-[120ms] ease-out';

  return (
    <Modal
      open={open}
      onClose={onClose}
      tone="info"
      placement="right"
      title="姿势库"
      summary="选择一个姿势套用到当前角色；可连续试用，右上角关闭。"
      footer={
        <>
          <button type="button" className="btn-ghost min-h-[30px]" onClick={onRest}>
            还原 rest
          </button>
          <button type="button" className="btn-ghost min-h-[30px]" onClick={onClose}>
            关闭
          </button>
          <span className="ml-auto self-center text-uiSm text-ink-muted">
            {catalog === null ? '加载中…' : `${results.length} / ${catalog.length}`}
          </span>
        </>
      }
    >
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="搜索姿势名称 / 分类 / 描述…"
        aria-label="搜索姿势"
        className="mb-2 w-full rounded-control border border-surface-line bg-surface-base px-2 py-1.5 text-uiSm text-ink-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
      />
      <div className="mb-2 flex flex-wrap gap-1">
        <button
          type="button"
          className={`${catBtn} ${category === ALL_CATEGORY ? 'border border-accent bg-surface-raised text-accent' : 'text-ink-secondary hover:bg-surface-raised'}`}
          aria-pressed={category === ALL_CATEGORY}
          onClick={() => setCategory(ALL_CATEGORY)}
        >
          {ALL_CATEGORY}
        </button>
        {cats.map((c) => (
          <button
            key={c}
            type="button"
            className={`${catBtn} ${category === c ? 'border border-accent bg-surface-raised text-accent' : 'text-ink-secondary hover:bg-surface-raised'}`}
            aria-pressed={category === c}
            onClick={() => setCategory(c)}
          >
            {c}
            <span className="ml-1 font-num text-ink-muted">{counts[c] ?? 0}</span>
          </button>
        ))}
      </div>

      {failed ? (
        <p className="py-6 text-center text-uiSm text-state-danger">姿势库加载失败，请刷新重试。</p>
      ) : catalog === null ? (
        <p className="py-6 text-center text-uiSm text-ink-muted">正在加载姿势库…</p>
      ) : results.length === 0 ? (
        <p className="py-6 text-center text-uiSm text-ink-muted">没有匹配的姿势。</p>
      ) : (
        <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-5">
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              title={`${p.name}｜${p.category}`}
              aria-pressed={activePoseId === p.id}
              onClick={() => onApply(p)}
              className={`flex flex-col items-center gap-0.5 rounded-control border p-1 transition-colors duration-[120ms] ease-out ${
                activePoseId === p.id
                  ? 'border-accent bg-surface-raised'
                  : 'border-surface-line hover:border-accent hover:bg-surface-raised'
              }`}
            >
              <img
                src={poseThumbUrl(p.id)}
                alt={p.name}
                loading="lazy"
                className="aspect-square w-full rounded-control bg-surface-base object-contain"
              />
              <span className="w-full truncate text-center text-uiSm text-ink-secondary">
                {p.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
